define((require) => {
    "use strict";

    const _                       = require("underscore");
    const $                       = require("jquery");
    const AddWidgetsAction        = require("ui/actions/AddWidgetsAction");
    const Backbone                = require("backbone");
    const Clipboard               = require("services/Clipboard");
    const EditorActivities        = require("editor/ui/experience/editor/activity/EditorActivities");
    const EditorActivityManager   = require("editor/ui/experience/editor/activity/EditorActivityManager");
    const EditorEvents            = require("shared/services/EditorEvents");
    const ExperienceEditor        = require("ui/editor/ExperienceEditor");
    const EditorEventFilters      = require("ui/editor/EditorEventFilters").default;
    const ExperiencesEditorZoom   = require("ui/editor/ExperiencesEditorZoom");
    const ImprovedView            = require("editor/zbone/ImprovedView");
    const Keycode                 = require("shared/services/KeyCode");
    const LayerEnum               = require("ui/editor/LayerEnum");
    const {Logger}                = require("shared/util/Logger");
    const Marionette              = require("marionette");
    const SceneEditor             = require("ui/editor/SceneEditor");
    const template                = require("ui/editor/ExperiencesEditor.tpl");
    const WidgetGroup             = require("ui/editor/WidgetGroup");
    const WidgetTypes             = require("shared/ui/experience/widgets/WidgetTypes");
    const ZoomView                = require("ui/editor/ZoomView");
    const MathUtil                = require("shared/util/MathUtil").default;

    const log = new Logger("ExperiencesEditor");

    const ExperienceEditorCollectionView = Marionette.CollectionView.extend({
        itemView:  ExperienceEditor
    });

    const UNICODE_EMPTY_CIRCLE = "&#9675;";
    const UNICODE_FILLED_CIRCLE = "&#9679;";
    const LAYER_SELECTED_ICON_SELECTOR = ".layer-selected-icon";

    const SCENE_MARGIN_TOP_PX = 75;
    const BOTTOM_BAR_HEIGHT_PX = 50;
    const VERTICAL_RULER_WIDTH_PX = 16;
    const EXPERIENCE_REGION_PADDING_RIGHT_PX = 50;

    const UNDO = "undo";
    const REDO = "redo";
    const GROUP = "group";
    const UNGROUP = "ungroup";
    // Superficial editor activity events are those produced by widgets that should not be added for Undo/Redo
    // in these cases, we handle their Undo/Redo functionality with a custom EditorActivity
    const SUPERFICIAL = "superficial";

    const ignoredActivitiesList = ["isSelected", "isTransforming"];
    /**
     * Experiences Editor is container for the collection of experience editors.
     *
     * @class ExperiencesEditor
     * @cfg {EditorViewModel} model The view model for the editor view.
     */
    return ImprovedView.extend({

        template,

        templateHelpers() {
            return {
                spectatorMode: this.viewScope.spectatorMode,
                LayerEnum
            };
        },

        // the active experience(s) within the editor
        activeExperience: null,

        // scrollbar handler
        scrollbars: null,

        _experienceCollectionView: null,

        _zoomController: null,

        _viewModel: null,

        _zoomModel: null,

        isRulerVisible: null,

        horizontalRuler: null,

        verticalRuler: null,

        verticalRulerMask: null,

        activeWidgetElement: null,

        events: {
            "click .add-new-scene": "addScene",
            "click @ui.changeLayerLinks": "_changeLayer",
            "click @ui.undo": UNDO,
            "click @ui.redo": REDO
        },

        keyboard: {
            "delete":     "_deleteSelection",
            "back_space": "_deleteSelection",
            "m":          "_toggleMasterLayers",
            "down":       "_arrowKeyHandler",
            "up":         "_arrowKeyHandler",
            "left":       "_arrowKeyHandler",
            "right":      "_arrowKeyHandler"
        },

        ui: {
            editAreaContainer: ".editor-area-container",
            viewport: ".viewport",
            overview: ".overview",
            rulersSwitch: ".rulers-switch",
            otherSwitch: ".other-switch",
            scrollTarget: ".scroll-target",
            scrollbarV: ".vertical",
            scrollbarH: ".horizontal",
            snappingSwitch: ".snapping-switch",
            layerLabel: ".layer-label",
            changeLayerLinks: ".change-layer",
            addNewSceneBtn: ".add-new-scene",
            undo: ".editor-undo",
            redo: ".editor-redo"
        },

        regions: {
            experienceRegion: "#experiences-region",
            experienceSyncStateRegion: ".experience-sync-state-region",
            zoomRegion: ".zoom-region"
        },

        modelEvents: {
            "change:sceneNumber": "_scrollToScene"
        },

        viewScope: {

            getScrollbarH() {
                return this.ui.scrollbarH;
            },

            getScrollbarV() {
                return this.ui.scrollbarV;
            },

            /**
             * Invoked when a <tt>SceneEditor</tt> is clicked.
             *
             * Updates the view model with the current scene number and
             * navigates/scrolls to the current scene in the editor.
             *
             * @param  {Scene} scene The scene model.
             */
            onSceneEditorClick(scene) {
                this._updateCurrentScene(scene);
            },

            onWidgetsAdded(widgets) {
                this._viewModel.selection.select(widgets);
                this._updateCurrentScene(this._viewModel.get("scene"));
            }
        },

        _toggleAddNewSceneEnabled() {
            this.ui.addNewSceneBtn.attr("disabled", this.activeExperience.get("emailModeEnabled"));
        },

        initialize() {
            this.activeExperience  = this.model.get("experience");

            this.listenTo(Clipboard, "copy", this._copyHandler);
            this.listenTo(Clipboard, "paste", this._pasteHandler);

            this._setupActivityManager();

            // Add Zooming feature
            this._zoomController = new ExperiencesEditorZoom(this);

            // Widget group event listeners
            this.listenTo(WidgetGroup, "group", this._widgetGroupHandler);
            this.listenTo(WidgetGroup, "ungroup", this._widgetUngroupHandler);

            this.listenTo(this.model, "change:currentLayer", this._setActiveLayerIconAndLabel);
        },

        _updateClipboarData() {
            const data = Clipboard.getData();
            Clipboard.setData(Object.assign({}, data, {isEmailModeEnabled: this.model.get("experience").get("emailModeEnabled")}));
        },

        onRender() {
            this._viewModel = this.viewScope.viewModel;

            this._zoomModel = this._viewModel.get("zoomModel");

            this.zoomRegion.show(new ZoomView({
                zoomModel: this._zoomModel,
                persistZoom: true,
                experienceId: this._viewModel.get("experience").get("id"),
                event: EditorEvents.constants.ZOOM_SCENE
            }));

            this.listenTo(this._viewModel, "change:showMasterLayers", (model, value, options) => {
                if (options.triggerOrigin) {
                    this._triggerEditorEvent(EditorEvents.constants.OPEN_MASTER_LAYERS, {
                        triggerOrigin: options.triggerOrigin,
                        toggle: value ? "on" : "off"
                    });
                }
            });

            this.listenTo(this.activeExperience, "change:emailModeEnabled", this._toggleAddNewSceneEnabled);
            this.listenTo(this._viewModel, "change:rulersShown", this._toggleRulersAndGuidelines);

            this.ui.rulersSwitch.on("switchChange.bootstrapSwitch", (event, state) => {
                this._viewModel.set("rulersShown", state, "rulersSwitch");
            });

            this.listenTo(this._viewModel, "change:snappingEnabled", this._toggleSnapping);

            this.ui.snappingSwitch.on("switchChange.bootstrapSwitch", (event, state) => {
                this._viewModel.set("snappingEnabled", state, "snappingSwitch");
            });

            this.listenTo(this._zoomModel, "change:scale", this._setOverviewWidth.bind(this));
            this.listenTo(this.model.get("experience"), "change:width", this._setOverviewWidth.bind(this));
            this.listenTo(this.model.get("experience").get("scenes"), "add remove", this._setOverviewWidth.bind(this));
        },

        onShow() {
            this._experienceCollectionView = new ExperienceEditorCollectionView({
                collection: new Backbone.Collection(this.activeExperience)
            });

            this.listenTo(this.model.get("experience"), "change:emailModeEnabled", this._updateClipboarData);

            this.experienceRegion.show(this._experienceCollectionView);

            // Enable the jquery tiny scroll bar
            this.ui.viewport.tinyscrollbar({
                x: {trackSize: 400, scrollbar: this.ui.scrollbarH},
                y: {trackSize: 300, scrollbar: this.ui.scrollbarV, extraContentSize: SCENE_MARGIN_TOP_PX + BOTTOM_BAR_HEIGHT_PX}
            });

            this.scrollbars = this.ui.viewport.data("tinyscrollbar");

            // Get the contentOffsetfor Y axis from the tinyscrollbar.
            this.ui.viewport.bind("move", () => {
                this._viewModel.set("contentOffsetY", this.scrollbars.y.options.contentOffset);
            });
            const self = this;
            // Using event delegation to capture clicks from all elements except things inside the editor surface.
            this.ui.viewport.bind("click", (event) => {
                // Being safe even thought most non-IE browsers support this method.
                if (event.target.closest && !event.target.closest(".scene-editor") && !self._viewModel.get("isSelectingElements")) {
                    this._viewModel.selection.clear();
                }

                self._viewModel.set("isSelectingElements", false);
            });

            // Scroll to the current scene, whatever it is...
            // For some certain size of experience, in the beginning, it does not scroll to the center, timeout is a fix.
            setTimeout(() => {
                this._scrollToScene(this.model.getCurrentSceneIndex());
            }, 700);

            // "cresize" is our custom jQuery.resize event.
            this.ui.viewport.on("cresize", () => {
                this.scrollbars.x.update();
                this.scrollbars.y.update();
            });

            // On load load the current snapping and rulers state
            this._toggleRulersAndGuidelines(this._viewModel, this._viewModel.get("rulersShown"));

            this.ui.rulersSwitch.bootstrapSwitch("state", this._viewModel.get("rulersShown"));
            this.ui.snappingSwitch.bootstrapSwitch("state", this._viewModel.get("snappingEnabled"));
            this._setOverviewWidth();
            this._setActiveLayerIconAndLabel();
            this._toggleAddNewSceneEnabled();
        },

        _setOverviewWidth() {
            const scale = this._zoomModel.get("scale");

            const oneSceneWidth = (SceneEditor.SCENE_MARGIN + VERTICAL_RULER_WIDTH_PX + this.model.get("experience").get("width") + SceneEditor.SCENE_MARGIN);
            const experiencesRegionWidth = (oneSceneWidth * this.model.get("experience").get("scenes").length) + EXPERIENCE_REGION_PADDING_RIGHT_PX;

            this.ui.overview.width(experiencesRegionWidth * (scale));
        },

        _toggleSnapping(viewModel, state, triggerOrigin) {
            // Make sure the switch state is in sync in case this was triggered by the context menu
            this.ui.snappingSwitch.bootstrapSwitch("state", state, true);

            if (triggerOrigin) {
                const eventProperties = this._getEventProperties({
                    toggle: state ? "on" : "off",
                    triggerOrigin
                });
                this._triggerEditorEvent(EditorEvents.constants.TOGGLE_SNAPPING, eventProperties);
            }
        },

        _toggleRulersAndGuidelines(viewModel, state, triggerOrigin) {
            // Make sure the switch state is in sync in case this was triggered by the context menu
            this.ui.rulersSwitch.bootstrapSwitch("state", state, true);
            this.ui.editAreaContainer.toggleClass("hide-rulers-and-guidelines", !state);

            if (triggerOrigin) {
                const eventProperties = this._getEventProperties({
                    toggle: state ? "on" : "off",
                    triggerOrigin
                });
                this._triggerEditorEvent(EditorEvents.constants.TOGGLE_RULERS_AND_GUIDELINES, eventProperties);
            }
        },

        /**
         * Sends Mix Panel event for the variant.
         * @param eventName The name of the event.
         * @param options additional options for the tracking method.
         * @private
         */
        _triggerEditorEvent(eventName, options) {
            const experience = this._viewModel.get("experience");
            const eventProperties = {
                experienceId: experience.get("id"),
                experienceVersion: experience.get("version"),
                experiencePushedVersion: experience.get("pushedVersion"),
                sceneNumber: this._viewModel.get("sceneNumber"),
                projectId: this.options.project && this.options.project.get("id"),
                triggerOrigin: options.triggerOrigin,
                toggle: options.toggle ? options.toggle : null,
                zoomType: options.zoomType ? options.zoomType : null
            };

            EditorEvents.trigger(eventName, eventProperties);
        },

        _updateCurrentScene(scene) {
            const sceneNumber = scene.get("sceneNumber");

            // Eventually invoking scrollToScene - see #modelEvents
            this._viewModel.set("sceneNumber", sceneNumber);
        },

        /**
        * Handle widget grouping
        */
        _widgetGroupHandler(options = {}) {
            const scene = this._viewModel.get("activeLayerSceneModel");
            const widgetModels = this._viewModel.selection.get("models");

            scene.groupWidgets(widgetModels, options, () => {
                if (options.eventProperties) {
                    const eventProperties = this._getGroupEventProperties(options.eventProperties, widgetModels);
                    EditorEvents.trigger(EditorEvents.constants.GROUP_WIDGETS, eventProperties);
                }
            });
        },

        /**
        * Handle widget ungrouping
        */
        _widgetUngroupHandler(options = {}) {
            const scene = this._viewModel.get("activeLayerSceneModel");
            const widgetModels = this._viewModel.selection.get("models");

            scene.ungroupWidgets(widgetModels, options, () => {
                if (options.eventProperties) {
                    const eventProperties = this._getGroupEventProperties(options.eventProperties, widgetModels);
                    EditorEvents.trigger(EditorEvents.constants.UNGROUP_WIDGETS, eventProperties);
                }
            });
        },

        /**
        * Set default event properties
        */
        _getEventProperties(eventProperties) {
            const experience = this._viewModel.get("experience");
            const project = this._viewModel.get("project");

            eventProperties = _.defaults(eventProperties, {
                experienceId: experience.get("id"),
                experienceVersion: experience.get("version"),
                experiencePushedVersion: experience.get("pushedVersion"),
                triggerOrigin: eventProperties.triggerOrigin
            });

            if (project) {
                eventProperties.projectId = project.get("id");
            }

            return eventProperties;
        },

        /**
        * Return group event properties for Mixpanel Events
        */
        _getGroupEventProperties(eventProperties, widgetModels) {
            eventProperties = this._getEventProperties(eventProperties);

            if (widgetModels) {
                const widgetTypes = _.map(widgetModels, (widgetModel) => widgetModel.get("type"));
                eventProperties.numberOfWidgetSelected = widgetModels.length;
                eventProperties.widgetTypes = widgetTypes.join();
            }

            return eventProperties;
        },

        /**
         * Add a new scene to the experience, cascading to all variants as well. <p>
         *
         * Will wait for all variants to be stable, i.e. no pending sync request, before adding a new scene. If we
         * do not do this, we can get structural changes that differs across variants more easily!
         */
        addScene() {
            const experience = this.model.get("experience");
            const newScenePosition = this.model.getCurrentSceneIndex() + 1;

            this._viewModel.selection.clear();
            log.info(`Adding scene at index ${newScenePosition}: ${experience}`, experience);
            experience.addScene(newScenePosition); // fire!
        },

        /**
         * Scrolls to the specified scene.
         *
         * @param  {Scene|String|Number} scene The <tt>Scene</tt> instance or the raw scene number.
         */
        _scrollToScene(scene) {
            const sceneNumberValue = (_.isNumber(scene) || _.isString(scene)) ? scene : scene.get("sceneNumber");
            let sceneNumber = parseInt(sceneNumberValue, 10);

            sceneNumber = sceneNumber > 0 ? sceneNumber : 0;

            const margin   = SceneEditor.SCENE_MARGIN * 2 * sceneNumber;
            const scale    = this._zoomModel.get("scale");
            const position = this.activeExperience.get("width") * scale * (sceneNumber - 0.5) + margin;

            this.scrollbars.x.update().scrollTo("center", position, false, true);
        },

        /**
         * Returns the viewport element
         */
        getViewportElement() {
            return this.ui.viewport;
        },

        /**
         * @private
         *
         * Initializes the <tt>EditorActivityManager</tt>, enabling undo/redo support.
         */
        _setupActivityManager() {
            const sceneCollection = this.activeExperience.get("scenes");

            this.listenTo(sceneCollection, "add", this._undoRedoEnabler);
            this.listenTo(sceneCollection, "remove", this._undoRedoDisabler);

            sceneCollection.each(this._undoRedoEnabler.bind(this));

            this._undoRedoEnabler(this.activeExperience.get("overlayScene"));
            this._undoRedoEnabler(this.activeExperience.get("backgroundScene"));
        },

        /**
         * @private
         *
         * Registers a change to widget model if change meets criteria for undo/redo eligibility.
         *
         * @param  {WidgetModel} widgetModel The widget model.
         * @param  {Options} options The change options.
         */
        _registerChangeOnModel(widgetModel, options) {
            // Bail out if the change was caused by a selection, undo, redo, group or ungroup action
            const changedAttributes = widgetModel.changedAttributes();
            const isIgnordAttribute = Object.keys(changedAttributes).some((attr) => ignoredActivitiesList.includes(attr));
            if (isIgnordAttribute) {
                return;
            }

            const {cause} = options;
            if (cause) {
                const cause = options.cause;

                if (cause === UNDO || cause === REDO || cause === GROUP || cause === UNGROUP || cause === SUPERFICIAL) {
                    return;
                }
            }

            // Register the change to enable undo/redo
            EditorActivityManager.add(new EditorActivities.WidgetPropertiesChangedActivity(
                widgetModel,
                widgetModel.changedAttributes(),
                widgetModel.previousAttributes()
            ));
        },

        /**
         * @private
         *
         * Registers listeners to enable undo/redo.
         *
         * @param  {Scene} sceneModel The scene model.
         */
        _undoRedoEnabler(sceneModel) {
            const widgetCollection = sceneModel.get("widgets");
            // Listen for any changes in the widget model to enable undo/redo for widget changes
            this.listenTo(widgetCollection, "change", (widgetModel, options) => {
                this._registerChangeOnModel(widgetModel, options);
            });

            const isDynamicProduct = (widgetModel) => {
                const {attributes} = widgetModel;
                return attributes && attributes.type === WidgetTypes.DYNAMIC_PRODUCT.id && attributes.group;
            };
            // Listen for any changes in widget models nested under a Dynamic Product widget
            widgetCollection.models.forEach((model) => {
                const {attributes} = model;
                if (isDynamicProduct(model)) {
                    this.listenTo(attributes.group, "change", (widgetModel, options) => {
                        this._registerChangeOnModel(widgetModel, options);
                    });
                }
            });

            // We would like to know when a widget is added or removed
            this.listenTo(widgetCollection, "add remove", (widgetModel, collection, options) => {
                // Bail out if the change was caused by a undo, redo, group or ungroup action
                if (options && options.cause) {
                    const cause = options.cause;

                    if (cause === UNDO || cause === REDO || cause === GROUP || cause === UNGROUP || cause === SUPERFICIAL) {
                        return;
                    }
                }

                // If the widget was added
                if (options.add) {
                    EditorActivityManager.add(new EditorActivities.WidgetCreatedActivity(widgetModel, sceneModel));
                    if (isDynamicProduct(widgetModel)) {
                        const {attributes} = widgetModel;
                        options.cause = EditorEventFilters.SUPERFICIAL;
                        this.listenTo(attributes.group, "change", (widgetModel, options) => {
                            this._registerChangeOnModel(widgetModel, options);
                        });
                    }
                // Widget was removed
                } else {
                    EditorActivityManager.add(new EditorActivities.WidgetDeletedActivity(widgetModel, sceneModel));
                }
            });
        },

        /**
         * @private
         *
         * Cleans up listeners used to perform undo/redo.
         *
         * @param  {Scene} sceneModel The scene model removed from the experience.
         */
        _undoRedoDisabler(sceneModel) {
            this.stopListening(sceneModel.get("widgets"));
        },

        /**
         * Delete currently selected widgets
         */
        _deleteSelection(event) {
            if (!$(document.activeElement).is("input, textarea") && document.activeElement.getAttribute("contenteditable") !== "true") {
                event.preventDefault();
                this.model.deleteSelection({
                    triggerOrigin: "CancelOrDeleteKey"
                });
            }
        },

        /**
         * Handles arrow key event, which moves selected widgets
         * @param {Event} event The key event
         * @private
         */
        _arrowKeyHandler(event) {
            const amount  = event.shiftKey ? 10 : 1;
            const tagname = event.target.tagName.toUpperCase();
            const isHighlighted = this.model.get("activeLayerSceneModel").get("widgets").models.some((widget) => widget.get("highlighted"));

            if (tagname === "INPUT" || tagname === "TEXTAREA" ||
                (!!document.activeElement && document.activeElement.getAttribute("contenteditable") === "true") ||
                // don't move selected widgets if one of them is highlighted
                isHighlighted) {
                return;
            }

            switch (event.keyCode) {
                case Keycode.LEFT:
                    this.model.selection.moveBy("x", -amount);
                    break;

                case Keycode.RIGHT:
                    this.model.selection.moveBy("x", amount);
                    break;

                case Keycode.UP:
                    this.model.selection.moveBy("y", -amount);
                    break;

                case Keycode.DOWN:
                    this.model.selection.moveBy("y", amount);
                    break;

                default: // No need to propagate here.
            }
        },

        /**
         * Copy a widget(s)
         * @private
         */
        _copyHandler() {
            let selectedWidgets = this.model.selection.get("widgets");
            const currentScene = this._viewModel.get("scene");

            if (selectedWidgets) {
                // the order of widgets is the order of selection,
                // so we need to reserve the order of selected widgets
                selectedWidgets = _.sortBy(selectedWidgets, (widget) =>
                    widget.model.get("layerIndex"));

                const experienceSize = this.activeExperience.getSize();

                Clipboard.setData({
                    type: Clipboard.DataTypes.WIDGET,
                    experience: this.activeExperience,
                    sceneId: currentScene.cid,
                    models: _.map(selectedWidgets, (w) => w.model.clone({duplicate: true})), // since it can be pasted in and persisted
                    experienceWidth: experienceSize.width,
                    experienceHeight: experienceSize.height,
                    isEmailModeEnabled: this.activeExperience.get("emailModeEnabled")
                });
            }
        },

        /**
         * paste a widget(s) from previous copied data
         * @param {Object} copyData The copied data.
         * @param {Object} metadata The options for the paste.
         * @private
         */
        _pasteHandler(copyData, metadata) {
            const currentScene    = this._viewModel.get("scene");
            const experience      = this._viewModel.get("experience");
            const models          = _.invoke(copyData.models, "clone");
            const pasteCounts     = metadata.pasteCounts[currentScene.cid] || 0;
            const contextMenuUsed = metadata.position;
            const defaultOffset  = experience.getPercentagePosition({
                left: pasteCounts * 10,
                top:  pasteCounts * 10
            });

            if (currentScene && copyData.type === Clipboard.DataTypes.WIDGET) {
                metadata.pasteCounts[currentScene.cid] = contextMenuUsed ? pasteCounts : pasteCounts + 1;

                if (contextMenuUsed) {
                    metadata.position = experience.getPercentagePosition(metadata.position);
                }

                const topMostWidgetPosition = this._getTopLeftPosition(models);

                _.each(models, (model) => {
                    const widgetPosition = this._getWidgetPosition(model, contextMenuUsed, topMostWidgetPosition, metadata.position, defaultOffset);
                    model.set({
                        left: widgetPosition.left,
                        top:  widgetPosition.top
                    });

                    const currentExperienceSize = experience.getSize();

                    if (model.has("width")) {
                        const widgetWidth = MathUtil.round((model.get("scaledWidth") / currentExperienceSize.width) * copyData.experienceWidth, 1);
                        model.set({
                            scaledWidth: widgetWidth,
                            width: widgetWidth
                        });
                    }

                    if (model.has("height")) {
                        const widgetHeight = MathUtil.round((model.get("scaledHeight") / currentExperienceSize.height) * copyData.experienceHeight, 1);
                        model.set({
                            scaledHeight: widgetHeight,
                            height: widgetHeight
                        });
                    }
                });

                new AddWidgetsAction(currentScene, models, this.model).execute();
            }
        },

        _getTopLeftPosition(widgets) {
            if (_.isNull(widgets) || _.isEmpty(widgets)) {
                return {};
            }

            const topMostPosition = _.chain(widgets)
                .sortBy((widget) =>
                    widget.get("top"))
                .first()
                .value()
                .get("top");

            const leftMostPosition = _.chain(widgets)
                .sortBy((widget) =>
                    widget.get("left"))
                .first()
                .value()
                .get("left");

            return {top: topMostPosition, left: leftMostPosition};
        },

        _getWidgetPosition(widget, contextMenuUsed, topMostWidgetPosition, clickPosition, defaultOffset) {
            if (contextMenuUsed) {
                // Calculate the offset between this widget and the topmost widget
                const widgetOffset = {
                    left: topMostWidgetPosition.left - widget.get("left"),
                    top:  topMostWidgetPosition.top - widget.get("top")
                };
                // Update the widget position taking into account the above calculated widget offset
                return {
                    left: clickPosition.left - widgetOffset.left,
                    top:  clickPosition.top - widgetOffset.top
                };
            }

            // add a default offset so they don't overlay each other
            return {
                left: widget.get("left") + defaultOffset.left,
                top:  widget.get("top") + defaultOffset.top
            };
        },

        _toggleMasterLayers(event) {
            const tagname = event.target.tagName.toLowerCase();
            const showMasterLayers = !this._viewModel.get("showMasterLayers");

            if (tagname === "input" || tagname === "textarea" || !!event.target.getAttribute("contentEditable")) {
                return;
            }

            this._viewModel.set("showMasterLayers", showMasterLayers, {triggerOrigin: "KeyPress"});
        },

        _changeLayer(event) {
            event.preventDefault();

            const layerValue = $(event.currentTarget).attr("data-layer");
            const layer = LayerEnum.lookup(layerValue);

            this.model.set("currentLayer", layer);
        },

        _setActiveLayerIconAndLabel() {
            const activeLayer = this.model.get("currentLayer");

            this.ui.changeLayerLinks.each((i, element) => {
                const layer = $(element).attr("data-layer");

                if (LayerEnum.lookup(layer) === activeLayer) {
                    $(element).find(LAYER_SELECTED_ICON_SELECTOR).html(UNICODE_FILLED_CIRCLE);
                } else {
                    $(element).find(LAYER_SELECTED_ICON_SELECTOR).html(UNICODE_EMPTY_CIRCLE);
                }
            });

            this.ui.layerLabel.text(this.model.get("currentLayer").name);
        },

        onClose() {
            // Clear the history of the activity manager
            EditorActivityManager.clear();

            this._zoomController.close();
            this.ui.snappingSwitch.bootstrapSwitch("destroy", true);
        },

        undo() {
            if (EditorActivityManager.canUndo()) {
                EditorActivityManager.undo();
            }
        },

        redo() {
            if (EditorActivityManager.canRedo()) {
                EditorActivityManager.redo();
            }
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/editor/ExperiencesEditor.js